import { AddOnSelection } from '@Config/ticket-form-types'

interface EventInterface {
  track(eventName: AnalyticEvents, payload?: unknown): void
  trackDrawing(eventName: AnalyticEvents, drawingNumber: number, payload?: unknown): void
  trackPageView(pageTitle: string): void
  trackUser(clientId: string): void
}

export enum AnalyticEvents {
  ANIMATION_CHANGE = 'animation-change',
  EXIT_KENO = 'exit-keno',
  JUMP_TO_DRAWING = 'jump-to-drawing',
  LIST_TICKET = 'list-ticket',
  NEXT_DRAWING = 'next-drawing',
  PAGE_VIEW = 'gby-page-view-k2g',
  PAUSE_DRAWING = 'pause-drawing',
  PLAY_DRAWING = 'play-drawing',
  PREVIOUS_DRAWING = 'previous-drawing',
  RECEIPT_SUMMARY_VIEW = 'receipt-summary-view',
  RESTART_DRAWING = 'restart-drawing',
  SETTINGS_CHANGE = 'settings-change',
  SKIP_TO_END_OF_DRAWING = 'skip-to-end-of-drawing',
  USER_EVENT = 'gby-user-event-k2g',
  VIEWING_DRAWING = 'viewing-drawing',
  WATCH_TICKET = 'watch-ticket',
}

export type KenoEvent = {
  activeAnimations?: string
  activeSettings?: string
  addOnSelections?: AddOnSelection[]
  baseWager?: number
  drawingNumber?: number
  numberOfDraws?: number
  numberSelections?: number[]
  pageTitle?: string
  startingDrawNumber?: number
}

export type EventPayload = {
  event: string
  extraData: Partial<KenoEvent> & {
    eventTimestamp: string
    hostName: string | undefined
    playerIdentifier: string | undefined
  }
  pageTitle: string
}

class GoogleTagManager implements EventInterface {
  private pageTitle: string = 'unknown'
  private hostName?: string
  private playerIdentifier?: string

  private unpushedEvents: EventPayload[] = []

  track(eventName: AnalyticEvents, payload: Partial<KenoEvent> = {}): void {
    const event = {
      event: `gby-k2g-${eventName}`,
      extraData: {
        ...payload,
        eventTimestamp: new Date().toISOString(),
        hostName: this.hostName,
        playerIdentifier: this.playerIdentifier,
      },
      pageTitle: this.pageTitle,
    }

    if (!(this.hostName && this.playerIdentifier)) {
      this.unpushedEvents.push(event)
      return
    }

    this.pushIncompleteEvents()

    window.dataLayer.push(event)
    window.dataLayer.push(function () {
      // @ts-expect-error weird shadow reset https://developers.google.com/tag-platform/devguides/datalayer#reset
      this.reset()
    })
  }

  trackDrawing(eventName: AnalyticEvents, drawingNumber: number, payload: object = {}): void {
    this.track(eventName, { ...payload, drawingNumber })
  }

  trackPageView(pageTitle: string): void {
    this.pageTitle = pageTitle
    window.dataLayer.push({
      event: AnalyticEvents.PAGE_VIEW,
      eventTimestamp: new Date().toISOString(),
      hostName: this.hostName,
      pageTitle,
    })
  }

  trackUser(hostName: string, clientId?: string, playerId?: string): void {
    this.hostName = hostName

    if (clientId) {
      this.playerIdentifier = clientId
    } else if (playerId) {
      this.playerIdentifier = playerId
    }

    window.dataLayer.push({
      event: AnalyticEvents.USER_EVENT,
      eventTimestamp: new Date().toISOString(),
      userData: {
        id: clientId ?? playerId,
        hostName: this.hostName,
      },
    })
    this.pushIncompleteEvents()
  }

  private pushIncompleteEvents(): void {
    // This should only happen during app initialization where we might not have had all the user data loaded yet and we start recording events.
    // Next time we get an event, attempt to push all the older events
    if (this.unpushedEvents.length && this.hostName && this.playerIdentifier) {
      for (const e of this.unpushedEvents) {
        window.dataLayer.push(e)
      }
      this.unpushedEvents = []
    }
  }
}

const Analytics = new GoogleTagManager()

export default Analytics
